import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileArrowUp, CheckCircle, Spinner, WarningCircle, Trash, ChartLineUp,
  ArrowRight, Sparkle, ArrowsClockwise, FolderOpen, Download,
} from "@phosphor-icons/react";

export default function FinancialHealth() {
  const [items, setItems] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState("standalone"); // standalone | workspace
  const [selectedWs, setSelectedWs] = useState("");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fh, ws] = await Promise.all([
        api.get("/financial-health"),
        api.get("/workspaces"),
      ]);
      setItems(Array.isArray(fh.data) ? fh.data : []);
      setWorkspaces(Array.isArray(ws.data) ? ws.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load Financial Health module.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll analysing items every 4s
  useEffect(() => {
    const analysing = items.filter((i) => i.status === "analysing");
    if (analysing.length === 0) return;
    const t = setInterval(() => { load(); }, 4000);
    return () => clearInterval(t);
  }, [items, load]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (uploadTarget === "workspace" && selectedWs) fd.append("workspace_id", selectedWs);
    try {
      await api.post("/financial-health/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded — AI analysing…");
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this analysis?")) return;
    try {
      await api.delete(`/financial-health/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-screen-2xl" data-testid="financial-health-page">
      <div className="text-overline text-primary">AI Intelligence · v1.1</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Financial Health</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Upload Excel, CSV or trial-balance exports. Orixa parses the numbers and generates strengths, ratios,
        liquidity/profitability/solvency observations and 3 priority recommendations.
      </p>

      {/* Upload card */}
      <div className="mt-8 bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev" data-testid="fh-upload-card">
        <div className="flex items-center gap-2 text-overline text-muted-foreground mb-4">
          <Upload size={12} weight="bold" /> Upload a financial file
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attach to</label>
            <select
              value={uploadTarget}
              onChange={(e) => setUploadTarget(e.target.value)}
              className="mt-1 w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
              data-testid="fh-upload-target"
            >
              <option value="standalone">Standalone analysis</option>
              <option value="workspace">Attach to a workspace</option>
            </select>
          </div>
          {uploadTarget === "workspace" && (
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Workspace</label>
              <select
                value={selectedWs}
                onChange={(e) => setSelectedWs(e.target.value)}
                className="mt-1 w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                data-testid="fh-workspace-select"
              >
                <option value="">Select a workspace…</option>
                {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="mt-6 border-2 border-dashed border-border rounded-xl p-8 text-center">
          <FileArrowUp size={32} weight="duotone" className="mx-auto text-muted-foreground/60" />
          <div className="mt-3 font-display text-lg tracking-tight">Drop your Excel, CSV or trial-balance</div>
          <div className="text-xs text-muted-foreground mt-1">Supported: .xlsx · .xls · .csv · .tsv — max 8 MB</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.xlsm,.csv,.tsv"
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="hidden"
            data-testid="fh-file-input"
            disabled={uploading || (uploadTarget === "workspace" && !selectedWs)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || (uploadTarget === "workspace" && !selectedWs)}
            className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
            data-testid="fh-upload-btn"
          >
            {uploading ? <Spinner size={14} weight="bold" className="animate-spin" /> : <Upload size={14} weight="bold" />}
            {uploading ? "Uploading…" : "Choose file"}
          </button>
        </div>
      </div>

      {/* Analyses list */}
      <div className="mt-10">
        <h2 className="font-display text-2xl tracking-tight">Recent analyses</h2>
        {loading && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="fh-loading">
            <Spinner size={16} weight="bold" className="animate-spin text-primary" /> Loading…
          </div>
        )}
        {!loading && error && (
          <div className="mt-6 bg-card border border-border rounded-2xl p-6 max-w-xl" role="alert" data-testid="fh-list-error">
            <div className="inline-flex items-center gap-2 text-overline text-rose-600">
              <WarningCircle size={14} weight="bold" /> {error}
            </div>
            <button onClick={load} className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold">
              <ArrowsClockwise size={12} weight="bold" /> Retry
            </button>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="mt-6 border border-dashed border-border rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="fh-empty">
            No analyses yet. Upload a financial file to get started.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="mt-4 divide-y divide-border border border-border rounded-2xl overflow-hidden bg-card" data-testid="fh-list">
            {items.map((it) => (
              <Link
                key={it.id}
                to={`/financial-health/${it.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-secondary/40"
                data-testid={`fh-row-${it.id}`}
              >
                <div className="h-9 w-9 rounded-lg bg-teal-soft text-teal grid place-items-center shrink-0">
                  <ChartLineUp size={16} weight="bold" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base tracking-tight truncate">{it.label || it.filename}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-1 flex items-center gap-3 flex-wrap">
                    <span>{(it.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    {it.workspace_id && <span className="inline-flex items-center gap-1"><FolderOpen size={11} weight="bold" /> workspace</span>}
                    {it.status === "analysing" && <span className="inline-flex items-center gap-1 text-sky-600"><Spinner size={11} weight="bold" className="animate-spin" /> Analysing…</span>}
                    {it.status === "ready" && <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle size={11} weight="bold" /> Ready</span>}
                    {it.status === "failed" && <span className="inline-flex items-center gap-1 text-rose-600"><WarningCircle size={11} weight="bold" /> Failed</span>}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(it.id); }}
                  className="opacity-60 hover:opacity-100 hover:text-rose-600 transition-opacity"
                  data-testid={`fh-delete-${it.id}`}
                  aria-label="Delete"
                >
                  <Trash size={14} weight="bold" />
                </button>
                <ArrowRight size={16} weight="bold" className="text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-14 text-xs text-muted-foreground max-w-2xl">
        <Sparkle size={12} weight="fill" className="inline text-gold" /> Powered by Claude Sonnet 4.5 · Financial analysis is decision-support intelligence, not certified accounting advice.
      </div>
    </div>
  );
}
